import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import BenchlingConfig from 'BenchlingConfig';
import CreateEntryModal from 'folder-items/CreateEntryModal';
import CreateEntryFromTemplateModal from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import LegacyCreateEntryFromTemplateModal from 'notebook/entry-templates/LegacyCreateEntryFromTemplateModal';
import {BenchlingState} from 'redux-stores/reduceApp';
import {createFulfillmentDefinition} from 'requests/execution/CreateFulfillmentDefinitionUtil';
import {RequestFulfillmentDefinition} from 'requests/fulfillments/FulfillmentConstants';
import sortFulfillments from 'requests/fulfillments/sortFulfillments';
import {Request, RequestSchema} from 'requests/RequestConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {StudyAssociation} from 'studies/StudyableDAO';
import memoizeLastCall from 'util/memoizeLastCall';
import CreateWorkflowModal from 'workflows/modals/CreateWorkflowModal';

import {EXECUTION_TYPES, ExecutionType} from './ExecuteRequestFlowConstants';
import SelectExistingEntryModal from './existing-entry/SelectExistingEntryModal';

interface BaseExecutionPaneProps {
  requests: Array<Request>;
  requestSchemasByRequestId: {[requestId: string]: RequestSchema};
  executionType: ExecutionType;
  onRequestClose: () => void;
}

interface ConnectedProps {
  fulfillments: Array<RequestFulfillmentDefinition>;
}

function ExecutionPane(props: BaseExecutionPaneProps & ConnectedProps): JSX.Element {
  switch (props.executionType) {
    case EXECUTION_TYPES.ENTRY:
      return (
        <CreateEntryModal
          onEntryCreated={props.onRequestClose}
          onRequestClose={props.onRequestClose}
          fulfillments={props.fulfillments}
          showNotebook={true}
        />
      );
    case EXECUTION_TYPES.ENTRY_TEMPLATE:
      // TODO(BNCH-38468): Remove feature flag after rollout and discontinue lagacy modal
      if (!BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL) {
        return (
          <LegacyCreateEntryFromTemplateModal
            onRequestClose={props.onRequestClose}
            onRequestSubmit={({
              entryName,
              folderId,
              templateId,
              initialDay,
            }: {
              entryName: string;
              folderId: string;
              templateId: string;
              initialDay?: string;
            }) => {
              EntryActions.createFromTemplate({
                entryName,
                folderId,
                templateId,
                initialDay,
                fulfillments: props.fulfillments,
                onSuccess: props.onRequestClose,
              });
            }}
          />
        );
      } else {
        return (
          <CreateEntryFromTemplateModal
            onRequestClose={props.onRequestClose}
            onRequestSubmit={({
              entryName,
              folderId,
              templateId,
              tagSchemaId,
              schemaTags,
              initialDay,
              studyAssociations,
            }: {
              entryName: string;
              folderId: string;
              templateId: string;
              tagSchemaId?: string;
              schemaTags?: TagUpdateObject;
              initialDay?: string;
              studyAssociations?: Array<StudyAssociation>;
            }) => {
              EntryActions.createFromTemplate({
                entryName,
                folderId,
                templateId,
                tagSchemaId,
                schemaTags,
                initialDay,
                studyAssociations,
                fulfillments: props.fulfillments,
                onSuccess: props.onRequestClose,
              });
            }}
            isTemplateSelectorDisabled={false}
          />
        );
      }
    case EXECUTION_TYPES.EXISTING_ENTRY:
      return (
        <SelectExistingEntryModal onRequestClose={props.onRequestClose} fulfillments={props.fulfillments} />
      );
    case EXECUTION_TYPES.WORKFLOW:
      return <CreateWorkflowModal onRequestClose={props.onRequestClose} fulfillments={props.fulfillments} />;
    default:
      throw new Error(`Unexpected request execution type ${props.executionType}`);
  }
}

export function mapStateToProps(
  state: BenchlingState,
  {requests, requestSchemasByRequestId}: BaseExecutionPaneProps
): {fulfillments: Array<RequestFulfillmentDefinition>} {
  const {requestTaskIdsByRequestId, sampleGroupIdsByRequestId} = state.models.requests.inputs;
  const selectedFulfillmentDefinitions = state.ui.requests.executeRequestFlow.selectedFulfillmentDefinitions;

  if (
    requests.some(({id}) => requestTaskIdsByRequestId[id] == null || sampleGroupIdsByRequestId[id] == null)
  ) {
    throw new Error('Expected all tasks and sample groups to be loaded before reaching the execution pane.');
  }

  const fulfillments = [];
  for (const request of requests) {
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (requestSchemasByRequestId[request.id].sampleGroupSchema === null) {
      fulfillments.push(
        createFulfillmentDefinition({requestId: request.id, requestTaskId: null, sampleGroupId: null})
      );
    } else {
      const fulfillmentDefinitionsForRequest =
        selectedFulfillmentDefinitions != null
          ? selectedFulfillmentDefinitions.filter((definition) => definition.requestId === request.id)
          : [];
      fulfillments.push(...fulfillmentDefinitionsForRequest);
    }
  }

  const sortedFulfillments = sortFulfillments(fulfillments, {
    requestsById: _.indexBy(requests, 'id'),
    requestTaskIdsByRequestId,
    sampleGroupIdsByRequestId,
  });

  return {fulfillments: memoizeFulfillments(sortedFulfillments)};
}
const memoizeFulfillments = memoizeLastCall(
  (sortedFulfillments: Array<RequestFulfillmentDefinition>) => sortedFulfillments,
  {
    useDeepEquality: true,
  }
);

export default connect(mapStateToProps)(ExecutionPane);
